import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import { ExhibitorsCatalog } from './containers/ExhibitorsCatalog';
import { ArtworksCatalog } from './containers/ArtworksCatalog';
import { SiteSearch } from './containers/SiteSearch';
import { SiteSearchInput } from './containers/SiteSearchInput';
import { PressImageOverviewPublic } from './containers/PressImageOverviewPublic';
import { FairInteractiveMap } from './containers/FairInteractiveMap';
import { FairStaticMap } from './containers/FairStaticMap';
import { store } from './redux-modules/store';
import './suppressNextReactWarning';

window.waitForJQuery(() => {
  const exhibitorsCatalog = document.getElementById('catalog-exhibitors');
  if (exhibitorsCatalog) {
    const url = exhibitorsCatalog.getAttribute('data-url');
    const credentials = exhibitorsCatalog.getAttribute('data-credentials');
    const app = exhibitorsCatalog.getAttribute('data-app');
    const fair = exhibitorsCatalog.getAttribute('data-fair');
    const defaultValue = exhibitorsCatalog.getAttribute('data-default-value');
    const type = exhibitorsCatalog.getAttribute('data-type');
    ReactDOM.createRoot(exhibitorsCatalog).render(
      <React.StrictMode>
        <ExhibitorsCatalog
          url={url}
          credentials={credentials}
          app={app}
          fair={fair}
          defaultValue={defaultValue}
          type={type}
        />
      </React.StrictMode>,
    );
  }

  const artworksCatalog = document.getElementById('catalog-artworks');
  if (artworksCatalog) {
    const url = artworksCatalog.getAttribute('data-url');
    const credentials = artworksCatalog.getAttribute('data-credentials');
    const app = artworksCatalog.getAttribute('data-app');
    const fair = artworksCatalog.getAttribute('data-fair');
    const fairCurrency = artworksCatalog.getAttribute('data-fair-currency');
    const showFilters = artworksCatalog.getAttribute('data-show-filters');
    ReactDOM.createRoot(artworksCatalog).render(
      <React.StrictMode>
        <Provider store={store}>
          <ArtworksCatalog
            url={url}
            credentials={credentials}
            app={app}
            fair={fair}
            fairCurrency={fairCurrency}
            showFilters={showFilters}
          />
        </Provider>
      </React.StrictMode>,
    );
  }

  const siteSearch = document.getElementById('site-search');
  if (siteSearch) {
    const url = siteSearch.getAttribute('data-url');
    const credentials = siteSearch.getAttribute('data-credentials');
    const app = siteSearch.getAttribute('data-app');
    ReactDOM.createRoot(siteSearch).render(
      <React.StrictMode>
        <SiteSearch url={url} credentials={credentials} app={app} />
      </React.StrictMode>,
    );
  }

  const siteSearchInputs = document.getElementsByClassName('site-search-input');
  if (siteSearchInputs && siteSearchInputs.length > 0) {
    for (let i = 0; i < siteSearchInputs.length; i += 1) {
      const node = siteSearchInputs[i];
      const url = node.getAttribute('data-url');
      const credentials = node.getAttribute('data-credentials');
      const app = node.getAttribute('data-app');
      ReactDOM.createRoot(node).render(
        <React.StrictMode>
          <SiteSearchInput url={url} credentials={credentials} app={app} />
        </React.StrictMode>,
      );
    }
  }

  const pressImages = document.getElementById('pressimages-public');
  if (pressImages) {
    const url = pressImages.getAttribute('data-url');
    const app = pressImages.getAttribute('data-app');
    const credentials = pressImages.getAttribute('data-credentials');
    ReactDOM.createRoot(pressImages).render(
      <React.StrictMode>
        <PressImageOverviewPublic url={url} app={app} credentials={credentials} />
      </React.StrictMode>,
    );
  }

  const fairInteractiveMaps = document.getElementsByClassName('fair-interactive-map-container');
  if (fairInteractiveMaps && fairInteractiveMaps.length > 0) {
    for (let i = 0; i < fairInteractiveMaps.length; i += 1) {
      const fairInteractiveMap = fairInteractiveMaps[i];
      if (fairInteractiveMap) {
        const svg = fairInteractiveMap ? fairInteractiveMap.querySelector('svg') : null;
        if (svg) {
          const exhibitorsUrl = fairInteractiveMap.getAttribute('data-exhibitors-url');
          const exhibitorsCredentials = fairInteractiveMap.getAttribute('data-exhibitors-credentials');
          const exhibitorsApp = fairInteractiveMap.getAttribute('data-exhibitors-app');
          const artworksUrl = fairInteractiveMap.getAttribute('data-artworks-url');
          const artworksCredentials = fairInteractiveMap.getAttribute('data-artworks-credentials');
          const artworksApp = fairInteractiveMap.getAttribute('data-artworks-app');
          const fairTitle = fairInteractiveMap.getAttribute('data-fair-title');
          const showArtworks = !!fairInteractiveMap.getAttribute('data-show-artworks');
          const mapBgColor = fairInteractiveMap.getAttribute('data-map-bg-color');
          const hideHome = fairInteractiveMap.getAttribute('data-hide-home');
          const pois = JSON.parse(fairInteractiveMap.getAttribute('data-pois'));
          const floors = JSON.parse(fairInteractiveMap.getAttribute('data-floors'));

          ReactDOM.createRoot(fairInteractiveMap).render(
            <React.StrictMode>
              <FairInteractiveMap
                exhibitorsUrl={exhibitorsUrl}
                exhibitorsCredentials={exhibitorsCredentials}
                exhibitorsApp={exhibitorsApp}
                artworksUrl={artworksUrl}
                artworksCredentials={artworksCredentials}
                artworksApp={artworksApp}
                fairTitle={fairTitle}
                showArtworks={showArtworks}
                pois={pois}
                mapBgColor={mapBgColor}
                hideHome={!!hideHome}
                floors={floors}
              >
                {svg}
              </FairInteractiveMap>
            </React.StrictMode>,
          );
        }
      }
    }
  }

  const fairStaticMaps = document.getElementsByClassName('fair-static-map-container');
  if (fairStaticMaps && fairStaticMaps.length > 0) {
    for (let i = 0; i < fairStaticMaps.length; i += 1) {
      const fairStaticMap = fairStaticMaps[i];
      if (fairStaticMap) {
        const svg = fairStaticMap ? fairStaticMap.querySelector('svg') : null;
        if (svg) {
          const stand = fairStaticMap.getAttribute('data-stand');
          const pois = JSON.parse(fairStaticMap.getAttribute('data-pois'));
          const mapBgColor = fairStaticMap.getAttribute('data-map-bg-color');
          const onStandClickUrl = fairStaticMap.getAttribute('data-on-stand-click-url');

          ReactDOM.createRoot(fairStaticMap).render(
            <React.StrictMode>
              <FairStaticMap
                stand={stand}
                pois={pois}
                mapBgColor={mapBgColor}
                onStandClickUrl={onStandClickUrl}
              >
                {svg}
              </FairStaticMap>
            </React.StrictMode>,
          );
        }
      }
    }
  }
});
